type AM_PM = 'AM' | 'PM';

interface TimeReturnType {
  hour: string;
  minutes: string;
  seconds: string;
  am_pm: AM_PM;
}

/**
 * @description This function will return the time in AM/PM format
 *
 * @param {Date | string} date - The date object or string (if not Date object or string, it will throw an error)
 * @returns {TimeReturnType} - object containing hour, minutes, seconds and am_pm (AM/PM) values
 * @example - getTimeAM_PM(new Date()) => { hour: '12', minutes: '00', seconds: '00', am_pm: 'AM' }
 * @example - getTimeAM_PM("2024-05-08T18:30:45.000Z") => { hour: '06', minutes: '30', seconds: '45', am_pm: 'PM' }
 * @example - getTimeAM_PM("2024-05-08T12:00:00.000Z") => { hour: '12', minutes: '00', seconds: '00', am_pm: 'PM' }
 * @example - getTimeAM_PM("2024-05-08T05:15:30.000Z") => { hour: '05', minutes: '15', seconds: '30', am_pm: 'AM' }
 */

export const getTimeAM_PM = (date: Date | string): TimeReturnType => {
  if (!(date instanceof Date) && typeof date !== 'string') {
    throw new Error(
      'Invalid date format. Please provide a valid Date object or a string.'
    );
  }

  const dateData = date instanceof Date ? date : new Date(date);

  // Format the Date object to display time in AM/PM format
  const options = {
    hour: '2-digit' as const,
    minute: '2-digit' as const,
    second: '2-digit' as const,
    hour12: true,
  };
  const timeInAM_PMFormat = dateData.toLocaleTimeString('en-US', options);

  const am_pmStr = timeInAM_PMFormat.split(' ')[1];
  const timeStr = timeInAM_PMFormat.split(' ')[0];
  const [h, m, s] = timeStr.split(':');

  return {
    hour: h,
    minutes: m,
    seconds: s,
    am_pm: am_pmStr,
  } as TimeReturnType;
};
